/**
 * Promotion preview (TMMAPP-0015) — `GET /v1/me/promotions/{code}`.
 */
import type { PromotionPreview as Wire } from '@/api/generated';
import { request } from './client';
import { ErrorCodes, isApiError, isNotFound } from './errors';

export type PromotionKind = 'OFFER' | 'TRIAL_DAYS' | 'PERCENT_OFF_VARIABLE' | 'AMOUNT_OFF_VARIABLE';

const KINDS: ReadonlySet<string> = new Set([
  'OFFER',
  'TRIAL_DAYS',
  'PERCENT_OFF_VARIABLE',
  'AMOUNT_OFF_VARIABLE',
]);

export interface PromotionPreview {
  code: string;
  name: string;
  kind: PromotionKind | null;
  redeemable: boolean;
  reason: string | null;
  trialDays: number | null;
  statements: number | null;
  percentBp: number | null;
  amountMinor: number | null;
  currency: string | null;
  validUntil: string | null;
  retailOfferId: number | null;
}

export function notFoundPreview(code: string): PromotionPreview {
  return {
    code,
    name: '',
    kind: null,
    redeemable: false,
    reason: 'NOT_FOUND',
    trialDays: null,
    statements: null,
    percentBp: null,
    amountMinor: null,
    currency: null,
    validUntil: null,
    retailOfferId: null,
  };
}

export function normalizePromotionPreview(w: Wire, fallbackCode = ''): PromotionPreview {
  return {
    code: w.code || fallbackCode,
    name: w.name,
    kind: KINDS.has(w.kind) ? w.kind : null,
    redeemable: w.redeemable,
    reason: w.reason ?? null,
    trialDays: w.trialDays ?? null,
    statements: w.statements ?? null,
    percentBp: w.percentBp ?? null,
    amountMinor: w.amountMinor ?? null,
    currency: w.currency ?? null,
    validUntil: w.validUntil ?? null,
    retailOfferId: w.retailOfferId ?? null,
  };
}

export const promotionsApi = {
  async preview(code: string): Promise<PromotionPreview> {
    const trimmed = code.trim();
    try {
      return normalizePromotionPreview(
        await request<Wire>('GET', `/v1/me/promotions/${encodeURIComponent(trimmed)}`),
        trimmed,
      );
    } catch (error) {
      if (
        isApiError(error) &&
        (isNotFound(error) || error.code === ErrorCodes.PROMOTION_NOT_FOUND)
      ) {
        return notFoundPreview(trimmed);
      }
      throw error;
    }
  },
};
